import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { reorderRow } from './TableActions';
import type { TableBlock } from '@/types/report-payload';
import type { DragEvent as ReactDragEvent, RefObject } from 'react';

type UseRowDragProps = {
    disabled: boolean;
    block: TableBlock;
    tableRef: RefObject<HTMLTableElement>;
    getRowLabel: (rowIndex: number) => string;
    updateBlock: (next: TableBlock) => void;
};

type UseRowDragResult = {
    rowDragOverIndex: number | null;
    rowInsertPosition: 'above' | 'below' | null;
    rowInsertTop: number | null;
    rowInsertTransitionMs: number;
    rowGhostLabel: string;
    handleRowDragStart: (rowIndex: number, event: ReactDragEvent) => void;
    handleRowDragEnd: () => void;
};

/** 1×1 transparent GIF used to suppress the browser's default drag ghost. */
const TRANSPARENT_IMG = (() => {
    if (typeof document === 'undefined') return null;
    const img = new Image(1, 1);
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    return img;
})();

export const useRowDrag = ({
    disabled,
    block,
    tableRef,
    getRowLabel,
    updateBlock,
}: UseRowDragProps): UseRowDragResult => {
    const [rowDragOverIndex, setRowDragOverIndex] = useState<number | null>(null);
    const [rowDragSourceIndex, setRowDragSourceIndex] = useState<number | null>(null);
    const [rowInsertPosition, setRowInsertPosition] = useState<'above' | 'below' | null>(null);
    const [rowInsertTop, setRowInsertTop] = useState<number | null>(null);
    const [rowInsertTransitionMs, setRowInsertTransitionMs] = useState(120);
    const [rowGhostLabel, setRowGhostLabel] = useState('');
    const didDropRef = useRef(false);
    const rowDragOverIndexRef = useRef<number | null>(null);
    const rowInsertPositionRef = useRef<'above' | 'below' | null>(null);

    // ── helpers ──────────────────────────────────────────────────────────

    /** Find the tbody row closest to `clientY` by iterating all rows. */
    const findRowAtY = useCallback(
        (clientY: number): { row: HTMLTableRowElement; rowIndex: number } | null => {
            if (!tableRef.current) return null;
            const tbody = tableRef.current.tBodies[0];
            if (!tbody || tbody.rows.length === 0) return null;

            const rows = tbody.rows;
            let closest: { row: HTMLTableRowElement; rowIndex: number; dist: number } | null = null;

            for (let i = 0; i < rows.length; i++) {
                const row = rows[i] as HTMLTableRowElement;
                const rect = row.getBoundingClientRect();
                const mid = rect.top + rect.height / 2;
                const dist = Math.abs(clientY - mid);
                if (!closest || dist < closest.dist) {
                    closest = { row, rowIndex: i, dist };
                }
            }

            return closest ? { row: closest.row, rowIndex: closest.rowIndex } : null;
        },
        [tableRef],
    );

    /** Compute row insertion Y using offset* properties (relative to offsetParent). */
    const updateRowInsertTop = useCallback(
        (row: HTMLElement, position: 'above' | 'below') => {
            if (!tableRef.current) return;
            const tableOffsetTop = tableRef.current.offsetTop;
            const rawInsert =
                position === 'below'
                    ? tableOffsetTop + row.offsetTop + row.offsetHeight
                    : tableOffsetTop + row.offsetTop;
            const contentHeight = tableRef.current.scrollHeight || 0;
            const insertOffset = Math.max(0, Math.min(rawInsert, contentHeight));
            setRowInsertTop((prev) => {
                if (prev === null) {
                    setRowInsertTransitionMs(0);
                } else {
                    const delta = Math.abs(insertOffset - prev);
                    setRowInsertTransitionMs(delta > 80 ? 0 : 120);
                }
                return insertOffset;
            });
        },
        [tableRef],
    );

    // ── drag start ──────────────────────────────────────────────────────

    const handleRowDragStart = useCallback(
        (rowIndex: number, event: ReactDragEvent) => {
            if (disabled) return;
            didDropRef.current = false;
            rowDragOverIndexRef.current = rowIndex;
            rowInsertPositionRef.current = 'below';
            setRowDragSourceIndex(rowIndex);
            setRowDragOverIndex(rowIndex);
            setRowInsertPosition('below');
            setRowGhostLabel(getRowLabel(rowIndex));
            setRowInsertTransitionMs(120);
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', `row:${rowIndex}`);

            // Suppress default browser ghost image
            if (TRANSPARENT_IMG) {
                event.dataTransfer.setDragImage(TRANSPARENT_IMG, 0, 0);
            }
        },
        [disabled, getRowLabel],
    );

    // ── drop logic ──────────────────────────────────────────────────────

    const applyRowDrop = useCallback(
        (targetRowIndex: number, position: 'above' | 'below') => {
            if (rowDragSourceIndex === null) return;
            didDropRef.current = true;

            const rowCount = block.rows.length;
            const rawTarget = position === 'below' ? targetRowIndex + 1 : targetRowIndex;
            let targetIndex = rawTarget;
            if (rowDragSourceIndex < targetIndex) {
                targetIndex -= 1;
            }
            targetIndex = Math.max(0, Math.min(targetIndex, rowCount - 1));

            const result = reorderRow(block, rowDragSourceIndex, targetIndex);

            if (!result.ok) {
                toast.error('Não é possível reorganizar linhas mescladas.');
            } else if (result.block) {
                updateBlock(result.block);
            }

            setRowDragSourceIndex(null);
            setRowDragOverIndex(null);
            setRowInsertPosition(null);
            setRowInsertTop(null);
            setRowGhostLabel('');
        },
        [block, rowDragSourceIndex, updateBlock],
    );

    const updateRowIndicatorFromPoint = useCallback(
        (clientY: number) => {
            const match = findRowAtY(clientY);
            if (!match) return;

            const rect = match.row.getBoundingClientRect();
            const midpoint = rect.top + rect.height / 2;
            const position = clientY < midpoint ? 'above' : 'below';

            setRowDragOverIndex(match.rowIndex);
            setRowInsertPosition(position);
            rowDragOverIndexRef.current = match.rowIndex;
            rowInsertPositionRef.current = position;
            updateRowInsertTop(match.row, position);
        },
        [findRowAtY, updateRowInsertTop],
    );

    // ── document-level tracking (sole drop zone while a drag is in flight) ─

    useEffect(() => {
        if (disabled || rowDragSourceIndex === null) return;

        const handleDragOver = (event: DragEvent) => {
            event.preventDefault();
            if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
            updateRowIndicatorFromPoint(event.clientY);
        };
        const handleDrop = (event: DragEvent) => {
            event.preventDefault();
            if (rowDragOverIndexRef.current !== null) {
                applyRowDrop(rowDragOverIndexRef.current, rowInsertPositionRef.current ?? 'below');
            }
        };

        document.addEventListener('dragover', handleDragOver);
        document.addEventListener('drop', handleDrop);
        return () => {
            document.removeEventListener('dragover', handleDragOver);
            document.removeEventListener('drop', handleDrop);
        };
    }, [applyRowDrop, disabled, rowDragSourceIndex, updateRowIndicatorFromPoint]);

    // ── drag end ────────────────────────────────────────────────────────

    const handleRowDragEnd = useCallback(() => {
        if (!didDropRef.current && rowDragOverIndexRef.current !== null) {
            applyRowDrop(rowDragOverIndexRef.current, rowInsertPositionRef.current || 'below');
        }
        setRowDragSourceIndex(null);
        setRowDragOverIndex(null);
        setRowInsertPosition(null);
        setRowInsertTop(null);
        setRowGhostLabel('');
    }, [applyRowDrop]);

    return {
        rowDragOverIndex,
        rowInsertPosition,
        rowInsertTop,
        rowInsertTransitionMs,
        rowGhostLabel,
        handleRowDragStart,
        handleRowDragEnd,
    };
};
